import React, { useEffect, useMemo, useState } from 'react'
import {
  SHIPPING_COUNTRIES,
  SHIPPING_COUNTRY_CODES,
  type ShippingCountryCode,
  getStateLabel,
  getStateName,
  getStatesFor,
} from '../../../constants/shippingZones'
import type { CategoryItem } from '../../../../../gateways/breadstack/categoryApis'
import type { MailOrderShippingZone } from '../../../../../types/location'
import {
  toTaxDecimalString,
  toTaxInputValue,
  validateTaxPercent,
} from '../../../utils/shippingTax'
import { ZoneExcludedCategoriesField } from './ZoneExcludedCategoriesField'
import { ZoneShippingTaxField, type ZoneTaxMode } from './ZoneShippingTaxField'

interface Props {
  open: boolean
  /** `duplicate` submits like `add`; `initial` is the already-cloned zone. */
  mode: 'add' | 'edit' | 'duplicate'
  initial?: MailOrderShippingZone | null
  onSave: (zone: MailOrderShippingZone) => Promise<void> | void
  onClose: () => void
  saving: boolean
  categories: CategoryItem[]
  categoriesLoading: boolean
  /** `${country}-${state}` keys already used by OTHER zones. */
  takenRegions?: string[]
  /** Backend error from the last save attempt; dialog stays open. */
  saveError?: string | null
  onClearSaveError?: () => void
  /** Location-level shipping tax a zone inherits when it has no override. */
  defaultShippingTax: string
  /** Deep link: open with the custom shipping-tax input focused. */
  autoFocusField?: 'shipping_tax'
}

interface FormValues {
  name: string
  country: ShippingCountryCode
  state: string
  flat_rate: string
  minimum_order: string
  free_shipping_over: string
  excluded_categories: number[]
  tax_mode: ZoneTaxMode
  shipping_tax_percentage: string
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: '#5A6B7B',
  marginBottom: 6,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 12px',
  fontSize: 13,
  color: '#2C3E50',
  background: '#fff',
  border: '1px solid #E8ECF0',
  borderRadius: 8,
  outline: 'none',
  fontFamily: 'inherit',
}

const moneyInputStyle: React.CSSProperties = {
  ...inputStyle,
  paddingLeft: 28,
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'textfield',
}

const moneyWrapStyle: React.CSSProperties = { position: 'relative' }

const moneyPrefixStyle: React.CSSProperties = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: 13,
  color: '#B0BEC5',
  pointerEvents: 'none',
}

function emptyForm(defaultTax: string): FormValues {
  return {
    name: '',
    country: 'CA',
    state: '',
    flat_rate: '',
    minimum_order: '',
    free_shipping_over: '',
    excluded_categories: [],
    tax_mode: 'default',
    shipping_tax_percentage: toTaxInputValue(defaultTax),
  }
}

function fromInitial(
  zone: MailOrderShippingZone | null | undefined,
  defaultTax: string,
  focusTax: boolean,
): FormValues {
  if (!zone) return emptyForm(defaultTax)
  const hasOverride =
    zone.shipping_tax_percentage != null && zone.shipping_tax_percentage !== ''
  const country: ShippingCountryCode =
    zone.country === 'US' || zone.country === 'CA' ? zone.country : 'CA'
  return {
    name: zone.name ?? '',
    country,
    state: zone.state ?? '',
    flat_rate: zone.flat_rate ?? '',
    minimum_order: zone.minimum_order ?? '',
    free_shipping_over: zone.free_shipping_over ?? '',
    excluded_categories: [...(zone.excluded_categories ?? [])],
    // Deep link from Overview "Edit" lands on the custom input directly.
    tax_mode: hasOverride || focusTax ? 'custom' : 'default',
    // Pre-fill custom with the default so switching modes starts from it.
    shipping_tax_percentage: hasOverride
      ? toTaxInputValue(zone.shipping_tax_percentage)
      : toTaxInputValue(defaultTax),
  }
}

function toDecimalString(value: string): string {
  const num = Number.parseFloat(value)
  return Number.isFinite(num) && num >= 0 ? num.toFixed(2) : '0.00'
}

export const ShippingZoneDialog: React.FC<Props> = ({
  open,
  mode,
  initial,
  onSave,
  onClose,
  saving,
  categories,
  categoriesLoading,
  takenRegions = [],
  saveError = null,
  onClearSaveError,
  defaultShippingTax,
  autoFocusField,
}) => {
  const focusTax = autoFocusField === 'shipping_tax'
  const [form, setForm] = useState<FormValues>(() =>
    fromInitial(initial, defaultShippingTax, focusTax),
  )

  useEffect(() => {
    if (open) setForm(fromInitial(initial, defaultShippingTax, focusTax))
  }, [open, initial, defaultShippingTax, focusTax])

  const states = useMemo(() => getStatesFor(form.country), [form.country])
  const isDuplicateRegion =
    !!form.state && takenRegions.includes(`${form.country}-${form.state}`)
  const canSave =
    !!form.name.trim() &&
    !!form.country &&
    !!form.state &&
    !isDuplicateRegion &&
    (form.tax_mode === 'default' ||
      validateTaxPercent(form.shipping_tax_percentage) === null) &&
    !saving

  if (!open) return null

  const updateField = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    if (saveError) onClearSaveError?.()
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleSubmit = async () => {
    if (!canSave) return
    await onSave({
      name: form.name.trim(),
      country: form.country,
      state: form.state,
      flat_rate: toDecimalString(form.flat_rate),
      minimum_order: toDecimalString(form.minimum_order),
      free_shipping_over: toDecimalString(form.free_shipping_over),
      excluded_categories: form.excluded_categories,
      shipping_tax_percentage:
        form.tax_mode === 'custom'
          ? toTaxDecimalString(form.shipping_tax_percentage)
          : null,
    })
  }

  const isEdit = mode === 'edit'
  const stateLabel = getStateLabel(form.country)

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
        }}
      />
      <div
        style={{
          position: 'relative',
          background: '#fff',
          borderRadius: 12,
          width: 480,
          maxWidth: 'calc(100vw - 32px)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
          overflow: 'hidden',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 20px',
            borderBottom: '1px solid #E8ECF0',
          }}
        >
          <h3
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: '#2C3E50',
              margin: 0,
            }}
          >
            {isEdit
              ? 'Edit Shipping Zone'
              : mode === 'duplicate'
                ? 'Duplicate Shipping Zone'
                : 'Add Shipping Zone'}
          </h3>
          <button
            type="button"
            onClick={saving ? undefined : onClose}
            style={{
              padding: 4,
              background: 'none',
              border: 'none',
              cursor: saving ? 'not-allowed' : 'pointer',
              color: '#7F8C8D',
              opacity: saving ? 0.5 : 1,
            }}
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div
          style={{
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
          }}
        >
          <div>
            <label style={labelStyle} htmlFor="zone-name">Zone Name</label>
            <input
              id="zone-name"
              type="text"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="e.g. Ontario, Western Canada"
              style={inputStyle}
              disabled={saving}
            />
          </div>

          <div>
            <label style={labelStyle} htmlFor="zone-country">Country</label>
            <select
              id="zone-country"
              value={form.country}
              onChange={(e) => {
                updateField('country', e.target.value as ShippingCountryCode)
                updateField('state', '')
              }}
              style={inputStyle}
              disabled={saving}
            >
              {SHIPPING_COUNTRY_CODES.map((code) => (
                <option key={code} value={code}>
                  {SHIPPING_COUNTRIES[code]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle} htmlFor="zone-state">{stateLabel}</label>
            <select
              id="zone-state"
              aria-invalid={isDuplicateRegion}
              aria-describedby={isDuplicateRegion ? 'zone-state-error' : undefined}
              value={form.state}
              onChange={(e) => updateField('state', e.target.value)}
              style={inputStyle}
              disabled={saving}
            >
              <option value="">Select...</option>
              {states.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
            {isDuplicateRegion && (
              <p
                id="zone-state-error"
                role="alert"
                style={{ margin: '6px 0 0', fontSize: 12, color: '#DC2626' }}
              >
                A zone for {getStateName(form.country, form.state)} already
                exists
              </p>
            )}
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 12,
            }}
          >
            <div>
              <label style={labelStyle} htmlFor="zone-fee">Shipping Fee</label>
              <div style={moneyWrapStyle}>
                <span style={moneyPrefixStyle}>$</span>
                <input
                  id="zone-fee"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.flat_rate}
                  onChange={(e) => updateField('flat_rate', e.target.value)}
                  placeholder="0.00"
                  style={moneyInputStyle}
                  disabled={saving}
                />
              </div>
            </div>
            <div>
              <label style={labelStyle} htmlFor="zone-min">Min. Purchase</label>
              <div style={moneyWrapStyle}>
                <span style={moneyPrefixStyle}>$</span>
                <input
                  id="zone-min"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.minimum_order}
                  onChange={(e) => updateField('minimum_order', e.target.value)}
                  placeholder="0.00"
                  style={moneyInputStyle}
                  disabled={saving}
                />
              </div>
            </div>
            <div>
              <label style={labelStyle} htmlFor="zone-free">Free Shipping At</label>
              <div style={moneyWrapStyle}>
                <span style={moneyPrefixStyle}>$</span>
                <input
                  id="zone-free"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.free_shipping_over}
                  onChange={(e) => updateField('free_shipping_over', e.target.value)}
                  placeholder="0.00"
                  style={moneyInputStyle}
                  disabled={saving}
                />
              </div>
            </div>
          </div>

          <ZoneShippingTaxField
            mode={form.tax_mode}
            value={form.shipping_tax_percentage}
            defaultTax={defaultShippingTax}
            regionName={form.state ? getStateName(form.country, form.state) : ''}
            onModeChange={(m) => updateField('tax_mode', m)}
            onValueChange={(v) => updateField('shipping_tax_percentage', v)}
            disabled={saving}
            autoFocus={focusTax}
          />

          <ZoneExcludedCategoriesField
            value={form.excluded_categories}
            onChange={(ids) => updateField('excluded_categories', ids)}
            categories={categories}
            categoriesLoading={categoriesLoading}
            disabled={saving}
          />
        </div>

        {saveError && (
          <div
            role="alert"
            style={{
              margin: '0 20px 12px',
              padding: '8px 12px',
              fontSize: 12,
              color: '#B91C1C',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: 8,
              flexShrink: 0,
            }}
          >
            {saveError}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            padding: '14px 20px',
            borderTop: '1px solid #E8ECF0',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              color: '#374151',
              background: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSave}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              background: canSave ? '#2563EB' : '#9CA3AF',
              border: 'none',
              borderRadius: 8,
              cursor: canSave ? 'pointer' : 'not-allowed',
              opacity: canSave ? 1 : 0.7,
            }}
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Zone'}
          </button>
        </div>
      </div>
    </div>
  )
}
